<template>
  <!--
    PoolaGrowingCard — the countdown-week «عروض البولة بتكبر» card (design approved
    by the owner 1 Nov 2026). PoolaRun mounts it for phase "scheduled" only, right
    under the countdown, so it disappears by itself when the run opens.

    Two live numbers, never fabricated:
      • members' collective PoolShare — GET /clubs/poolshare-total through
        usePoolaTeaser, the same source the no-run hero (PoolaPoolHero) reads;
      • the توفير of the offers members can see — `deal.offersSavingsCents`
        (BE brief 2026-11-01-poola-deal-offers-savings-total), drawn against the
        fixed goal POOLA_GROWING_GOAL_CENTS. The bar fills as suppliers' offers
        are added during the week.

    Celebration: ONE confetti burst the first time the card appears in a session,
    and again when the savings grew since the member last saw them (a collective
    milestone — never a member's own action). Reduced motion → no burst at all
    (fireConfetti is gated). A CONTENT FRAGMENT — never its own <f7-page>.
  -->
  <section class="poola-growing">
    <h2 class="poola-growing__title">{{ t('poola.buy.growing.title') }}</h2>

    <p class="poola-growing__label">{{ t('poola.buy.growing.poolshareLabel') }}</p>
    <!-- The real total only once it has loaded — a shimmer until then, never a hard "0". -->
    <div v-if="hasLoaded" class="poola-growing__pool">
      <span class="poola-growing__pool-amount" dir="ltr">{{ poolDisplay }}</span>
      <span class="poola-growing__pool-currency">{{ t('poola.pool.currency') }}</span>
    </div>
    <div v-else class="poola-growing__skeleton" aria-hidden="true" />

    <p class="poola-growing__label">{{ t('poola.buy.growing.savingsLabel') }}</p>
    <div
      class="poola-growing__track"
      role="progressbar"
      :aria-label="t('poola.buy.growing.savingsLabel')"
      aria-valuemin="0"
      :aria-valuemax="goalEgp"
      :aria-valuenow="savingsEgp"
    >
      <div class="poola-growing__fill" :style="{ width: `${pct}%` }" />
    </div>
    <i18n-t
      keypath="poola.buy.growing.ofGoal"
      tag="p"
      class="poola-growing__of-goal"
      scope="global"
    >
      <template #current>
        <span class="poola-growing__current" dir="ltr">{{ savingsDisplay }}</span>
      </template>
      <template #goal>
        <span class="poola-growing__goal-amount" dir="ltr">{{ goalDisplay }}</span>
      </template>
    </i18n-t>

    <p class="poola-growing__more">{{ t('poola.buy.growing.moreComing') }}</p>
    <p class="poola-growing__goal">
      {{ t('poola.buy.growing.goalCaption', { goal: goalDisplay }) }}
    </p>
  </section>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePoolaTeaser } from '@/composables/usePoolaTeaser.js';
import { POOLA_GROWING_GOAL_CENTS } from '@/constants/poola.js';
import { fireConfetti } from '@/utils/confetti.js';

const props = defineProps({
  // The deserialised poola-deals resource (id + flattened attributes). Only
  // `id` and `offersSavingsCents` are read here.
  deal: {
    type: Object,
    required: true,
  },
  // Cents. The run's توفير goal; the bar is full at this value.
  goalCents: {
    type: Number,
    default: POOLA_GROWING_GOAL_CENTS,
  },
});

const { t } = useI18n({ useScope: 'global' });

// Collective club PoolShare (live, polled while the page is visible).
const { totalEgp, hasLoaded } = usePoolaTeaser();

// ── Western number formatter (never Arabic-Indic — owner-locked UI rule) ─────
const fmt = new Intl.NumberFormat('en-US');

// MONEY: integer cents in, one divide-by-100 at the display edge.
const savingsCents = computed(() =>
  Math.max(0, Math.round(Number(props.deal?.offersSavingsCents) || 0)),
);
const savingsEgp = computed(() => Math.round(savingsCents.value / 100));
const goalEgp = computed(() => Math.round(props.goalCents / 100));
const pct = computed(() => {
  if (!(props.goalCents > 0)) return 0;
  const raw = (savingsCents.value / props.goalCents) * 100;
  return Math.min(100, Math.round(raw * 10) / 10);
});

const poolDisplay = computed(() => fmt.format(Math.round(Number(totalEgp.value) || 0)));
const savingsDisplay = computed(() => fmt.format(savingsEgp.value));
const goalDisplay = computed(() => fmt.format(goalEgp.value));

// ── Celebration — once per session, and again when the number grew ──────────
// sessionStorage: "burst already shown this session" for this run.
// localStorage:   the savings the member last saw for this run, so a bigger
//                 number on the next open (or a live refetch) earns one more burst.
// Both reads/writes are wrapped: a private window or blocked storage must never
// break the card — it just celebrates on every open instead.
function storageKey(scope) {
  return `poola:growing:${scope}:${props.deal?.id ?? 'run'}`;
}
function readStore(store, key) {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}
function writeStore(store, key, value) {
  try {
    store.setItem(key, value);
  } catch {
    // storage unavailable — nothing to remember, nothing to break
  }
}

function celebrateIfDue() {
  if (typeof window === 'undefined') return;
  const lastSeen = Number(readStore(window.localStorage, storageKey('seen')));
  const grew = Number.isFinite(lastSeen) && lastSeen > 0 && savingsCents.value > lastSeen;
  const firstThisSession = readStore(window.sessionStorage, storageKey('burst')) !== '1';

  if (firstThisSession || grew) {
    fireConfetti();
    writeStore(window.sessionStorage, storageKey('burst'), '1');
  }
  writeStore(window.localStorage, storageKey('seen'), String(savingsCents.value));
}

onMounted(celebrateIfDue);
// A silent refetch that raised the savings while the card is on screen.
watch(savingsCents, (now, before) => {
  if (now > before) celebrateIfDue();
});
</script>

<style scoped>
/* Mirrors PoolaPoolHero's pool card: white, soft shadow, amber top rule. */
.poola-growing {
  margin: 0 16px 16px;
  padding: 20px;
  background: var(--poolit-bg-primary);
  border-radius: var(--poolit-radius-lg);
  box-shadow: var(--poolit-shadow-soft);
  border: 1px solid var(--poolit-border-color);
  border-top: 3px solid var(--poolit-warning-500);
  text-align: center;
}
.poola-growing__title {
  margin: 0 0 14px;
  font-size: var(--poolit-font-size-h3);
  line-height: var(--poolit-line-height-h3);
  font-weight: var(--poolit-font-weight-bold);
  color: var(--poolit-primary-900);
}
.poola-growing__label {
  margin: 0 0 6px;
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-semibold);
  letter-spacing: 0.04em;
  color: var(--poolit-text-secondary);
}
/* Members' PoolShare — the same amber figure as the no-run pool card. */
.poola-growing__pool {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
  margin: 0 0 18px;
}
.poola-growing__pool-amount {
  font-size: 2.75rem;
  font-weight: var(--poolit-font-weight-bold);
  color: var(--poolit-warning-600);
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.poola-growing__pool-currency {
  font-size: 1rem;
  font-weight: var(--poolit-font-weight-semibold);
  color: var(--poolit-warning-dark);
}
.poola-growing__skeleton {
  height: 44px;
  width: 60%;
  margin: 0 auto 18px;
  border-radius: var(--poolit-radius-md);
  background: var(--poolit-bg-secondary);
}
/* Offers savings — brand purple on a light purple track; fills from the start
   edge, so RTL fills from the right with no override. */
.poola-growing__track {
  width: 100%;
  height: 12px;
  background: var(--poolit-primary-100);
  border-radius: 999px;
  overflow: hidden;
  margin: 0 0 8px;
}
.poola-growing__fill {
  height: 100%;
  background: var(--poolit-primary-500);
  border-radius: 999px;
  transition: width 600ms ease-out;
}
@media (prefers-reduced-motion: reduce) {
  .poola-growing__fill {
    transition: none;
  }
}
.poola-growing__of-goal {
  margin: 0 0 14px;
  font-size: 15px;
  color: var(--poolit-text-secondary);
  font-variant-numeric: tabular-nums;
}
.poola-growing__current {
  font-size: 18px;
  font-weight: var(--poolit-font-weight-bold);
  color: var(--poolit-primary-900);
}
.poola-growing__goal-amount {
  font-weight: var(--poolit-font-weight-semibold);
}
.poola-growing__more {
  margin: 0 0 8px;
  font-size: var(--poolit-font-size-caption);
  line-height: 1.6;
  color: var(--poolit-text-secondary);
}
.poola-growing__goal {
  margin: 0;
  font-size: var(--poolit-font-size-small);
  color: var(--poolit-text-muted);
}
</style>
